import color from "color";
import Button from "components/Button/Button";
import Icon from "components/Icon/Icon";
import { StyledScrollBars } from "components/ScrollableContent/ScrollableContent";
import { fadeIn } from "react-animations";
import styled, { keyframes } from "styled-components";
import { buildDotGridSvg } from "styles/styles";

export const PANEL_WIDTH = "420px";
export const PANEL_SLIDE_MS = 300;

interface WrapperProps {
  $isOpen: boolean;
}

export const Wrapper = styled.div<WrapperProps>`
  user-select: text;
  z-index: 3;
  position: fixed;
  max-width: ${PANEL_WIDTH};
  width: 100%;
  height: 100%;
  right: 0;
  background-color: ${({ theme }) => theme.surfacePanel};
  background-image: ${({ theme }) => buildDotGridSvg(theme.surfaceTextureDot)};
  background-repeat: repeat;
  background-size: 5px 5px;
  transform: translateX(
    ${({ $isOpen }) => ($isOpen ? "0" : `calc(${PANEL_WIDTH} + 20px)`)}
  );
  box-shadow: ${({ theme }) => theme.panelShadow};
  transition: transform ${PANEL_SLIDE_MS}ms
    ${({ $isOpen }) => ($isOpen ? "ease-out" : "ease-in")};
  will-change: transform;

  ${({ $isOpen, theme }) => theme.xs`
    max-width: 100vw;
    box-shadow: none;
    transform: translateX(${$isOpen ? "0" : "100vw"});
    ${!$isOpen ? "transition-duration: 150ms;" : ""}
  `};
`;

export const Container = styled.aside`
  height: 100%;
  position: relative;
`;

export const LeftArrowIcon = styled(Icon)`
  height: 16px;
  width: 16px;
  background-color: ${({ theme }) => theme.greyInactive};
  margin-right: 6px;
  pointer-events: none;

  &&& {
    transition: opacity 0.2s;
  }
`;

export const BackButton = styled(Button)`
  font-size: 18px;
  display: flex;
  padding: 0;
  align-items: center;
  margin-right: 6px;

  &:hover {
    ${LeftArrowIcon} {
      opacity: 0.5;
    }
  }
`;

export const BackArrow = styled(Icon)`
  width: 10px;
  height: 16px;
  margin-right: 12px;
`;

export const CloseButton = styled.button`
  position: absolute;
  right: 15px;
  top: 15px;
  z-index: 2;

  .icon {
    transition: background-color 0.4s;
  }

  &:hover > .icon {
    background-color: ${({ theme }) => color(theme.iconDefault).darken(0.2).hex()};
  }
`;

export const ContentWrapper = styled.div`
  position: relative;
  display: flex;
  flex-grow: 1;
  flex-direction: column;
  align-items: stretch;
  height: 100%;
  max-height: 100%;
`;

interface HeadingProps {
  $isBordered?: boolean;
}

export const Heading = styled.div<HeadingProps>`
  position: relative;
  display: flex;
  align-items: center;
  margin: 0;
  font-size: 18px;
  font-weight: 400;

  ${({ theme }) =>
    theme.range(
      "padding",
      "15px",
      "22px",
      `${theme.breakpoints.lg}px`,
      `${theme.breakpoints.xl}px`,
    )};

  ${({ $isBordered, theme }) =>
    $isBordered && `border-bottom: 1px solid ${theme.black05}`};
`;

export const HeadingIcon = styled(Icon)`
  transition: background-color 0.3s;
  background-color: ${({ theme }) => theme.iconDefault};

  &:hover {
    background-color: ${({ theme }) => color(theme.iconDefault).darken(0.2).hex()};
  }
`;

interface ContentProps {
  disabled?: boolean;
  className?: string;
}

export const Content = styled(StyledScrollBars)<ContentProps>`
  animation: 0.3s ${keyframes`${fadeIn}`};

  & > div:first-child {
    padding: 15px;
  }

  ${(props) =>
    props.disabled &&
    `
      cursor: not-allowed;
      opacity: 0.5;
      pointer-events: none;
    `}
`;

export const PANEL_TRANSITION_MS = 150;

interface TransitionWrapperProps {
  $isTransitioning: boolean;
}

export const TransitionWrapper = styled.div<TransitionWrapperProps>`
  height: 100%;
  opacity: ${({ $isTransitioning }) => ($isTransitioning ? 0 : 1)};
  transition: opacity ${PANEL_TRANSITION_MS}ms ease-in-out;
`;

export const ButtonsWrapper = styled.div`
  width: 100%;
  display: ${(props) => (props.hidden ? "none" : "flex")};
  flex-wrap: wrap-reverse;
  justify-content: center;

  ${(props) => props.theme.xs`
    padding: 15px 0;
  `}

  button {
    margin: 5px;
  }
`;
